// The Files app's sheets: a name (new folder, rename), a move, and the actions
// on one file or folder. Every change is an ordinary op through `mutate`, so
// it is on undo; a folder's delete takes what is under it in the same group.

import { useState } from 'react'
import { ArrowRightLeft, Download, Folder, PencilLine, Share2, Star, Trash2 } from 'lucide-react'
import { mutate } from '@/runtime'
import type { FileResource } from '@/kinds/file/apply'
import { nameProblem } from '@/kinds/file'
import { childrenOf, descendants, isWithin, pathTo, sortFiles } from '@/lib/files/tree'
import { formatSize, typeLabel } from '@/lib/files/types'
import { Button, Field, Sheet, cx } from '@/components/ui'
import { useToasts } from '@/state/toasts'
import { downloadFile } from './parts'

/** A name for a new folder or a rename. Checked as the server checks it, and against its siblings. */
export const NameSheet = ({
  title,
  initial,
  siblings,
  action,
  onSave,
  onClose,
}: {
  title: string
  initial: string
  siblings: readonly FileResource[]
  action: string
  onSave: (name: string) => Promise<void>
  onClose: () => void
}) => {
  const [name, setName] = useState(initial)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    const t = name.trim()
    const why = nameProblem(t)
    if (why) return setError(why.charAt(0).toUpperCase() + why.slice(1))
    if (t !== initial && siblings.some((s) => s.spec.name.toLowerCase() === t.toLowerCase()))
      return setError('Something here already has that name')
    try {
      await onSave(t)
      onClose()
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <Sheet open onClose={onClose} title={title}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <Field label="Name" error={error ?? undefined}>
          <input
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setError(null)
            }}
            className="w-full"
            data-testid="files-name"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" data-testid="files-name-save">
            {action}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}

/** Where to move something: every folder it may go in (not itself or anything under it), and the top. */
export const MoveSheet = ({
  item,
  all,
  onClose,
}: {
  item: FileResource
  all: readonly FileResource[]
  onClose: () => void
}) => {
  const live = all.filter((f) => !f.deleted_at && f.labels.ws === item.labels.ws)
  const folders = sortFiles(
    live.filter((f) => f.spec.folder && !isWithin(live, item.id, f.id)),
    { key: 'name' },
  )
  const label = (f: FileResource) =>
    pathTo(live, f.id)
      .map((p) => p.spec.name)
      .join(' / ')
  const options = [
    { id: null as string | null, name: 'Files (the top)' },
    ...folders.map((f) => ({ id: f.id, name: label(f) })),
  ].sort((a, b) => (a.id === null ? -1 : b.id === null ? 1 : a.name.localeCompare(b.name)))
  const here = item.spec.parent ?? null
  const move = async (to: string | null) => {
    if (to === here) return onClose()
    const clash = childrenOf(live, to).some((s) => s.spec.name.toLowerCase() === item.spec.name.toLowerCase())
    if (clash) {
      useToasts.getState().show(`Something there is already called “${item.spec.name}”`)
      return
    }
    await mutate.patch(item.id, { spec: { parent: to } })
    onClose()
  }
  return (
    <Sheet open onClose={onClose} title={`Move “${item.spec.name}”`}>
      <ul className="group-list mb-2" data-testid="files-move">
        {options.map((o) => (
          <li key={o.id ?? 'top'}>
            <button
              type="button"
              className={cx(
                'w-full text-left px-3.5 min-h-11 flex items-center gap-2.5',
                o.id === here && 'text-faint',
              )}
              onClick={() => void move(o.id)}
              disabled={o.id === here}
            >
              <Folder size={18} strokeWidth={1.75} className="text-accent shrink-0" aria-hidden />
              <span className="truncate">{o.name}</span>
              {o.id === here && <span className="ml-auto text-xs">Here now</span>}
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  )
}

/** Delete a file, or a folder and everything under it, as one undoable group. */
export const deleteItem = async (item: FileResource, all: readonly FileResource[]) => {
  const under = item.spec.folder ? descendants(all, item.id) : []
  await mutate.grouped(async () => {
    for (const f of under) await mutate.remove(f.id)
    await mutate.remove(item.id)
  })
}

/** Restore a file, or a folder with what went into the Trash with it. */
export const restoreItem = async (item: FileResource, withIt: readonly FileResource[]) => {
  await mutate.grouped(async () => {
    await mutate.restore(item.id)
    for (const f of [...withIt].reverse()) await mutate.restore(f.id)
  })
}

/** What can be done with one file or folder. */
export const ActionsSheet = ({
  item,
  all,
  onShare,
  onClose,
  onDeleted,
}: {
  item: FileResource
  all: readonly FileResource[]
  onShare: () => void
  onClose: () => void
  /** after the delete (a file's own page leaves for its folder) */
  onDeleted?: () => void
}) => {
  const [mode, setMode] = useState<'menu' | 'rename' | 'move' | 'delete'>('menu')
  const siblings = childrenOf(all, item.spec.parent ?? null).filter((s) => s.id !== item.id)
  const under = item.spec.folder ? descendants(all, item.id) : []
  const ready = item.spec.folder || item.status.state === 'ready'
  if (mode === 'rename')
    return (
      <NameSheet
        title={item.spec.folder ? 'Rename folder' : 'Rename file'}
        initial={item.spec.name}
        siblings={siblings}
        action="Rename"
        onSave={(name) => mutate.patch(item.id, { spec: { name } })}
        onClose={onClose}
      />
    )
  if (mode === 'move') return <MoveSheet item={item} all={all} onClose={onClose} />
  const row = 'w-full text-left px-3.5 min-h-12 flex items-center gap-3 text-[15px]'
  return (
    <Sheet open onClose={onClose} title={item.spec.name}>
      <p className="text-sm text-muted -mt-1 mb-3">
        {item.spec.folder
          ? under.length
            ? `A folder of ${under.length}`
            : 'An empty folder'
          : typeLabel(item.spec.name, item.spec.content_type)}
        {!item.spec.folder && <span className="ml-3">{formatSize(item.spec.size)}</span>}
      </p>
      {mode === 'delete' ? (
        <div data-testid="files-delete-confirm">
          <p className="text-[15px] mb-3">
            Delete “{item.spec.name}” and the {under.length} {under.length === 1 ? 'thing' : 'things'} in it?
            Deleted keeps them for 30 days.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setMode('menu')}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => {
                void deleteItem(item, all).then(() => {
                  onClose()
                  onDeleted?.()
                })
              }}
              data-testid="files-delete-yes"
            >
              Delete all
            </Button>
          </div>
        </div>
      ) : (
        <ul className="group-list" data-testid="files-actions">
          {ready && (
            <li>
              <button type="button" className={row} onClick={onShare} data-testid="files-act-share">
                <Share2 size={18} aria-hidden /> Share
              </button>
            </li>
          )}
          {!item.spec.folder && ready && (
            <li>
              <button type="button" className={row} onClick={() => void downloadFile(item.id).then(onClose)}>
                <Download size={18} aria-hidden /> Download
              </button>
            </li>
          )}
          <li>
            <button
              type="button"
              className={row}
              onClick={() =>
                void mutate
                  .patch(item.id, { spec: { starred: item.spec.starred ? null : true } })
                  .then(onClose)
              }
            >
              <Star size={18} aria-hidden fill={item.spec.starred ? 'currentColor' : 'none'} />
              {item.spec.starred ? 'Unstar' : 'Star'}
            </button>
          </li>
          <li>
            <button
              type="button"
              className={row}
              onClick={() => setMode('rename')}
              data-testid="files-act-rename"
            >
              <PencilLine size={18} aria-hidden /> Rename
            </button>
          </li>
          <li>
            <button
              type="button"
              className={row}
              onClick={() => setMode('move')}
              data-testid="files-act-move"
            >
              <ArrowRightLeft size={18} aria-hidden /> Move
            </button>
          </li>
          <li>
            <button
              type="button"
              className={cx(row, 'text-danger')}
              data-testid="files-act-delete"
              onClick={() => {
                if (under.length) return setMode('delete')
                void deleteItem(item, all).then(() => {
                  onClose()
                  onDeleted?.()
                })
              }}
            >
              <Trash2 size={18} aria-hidden /> Delete
            </button>
          </li>
        </ul>
      )}
    </Sheet>
  )
}
